/**
 * Single source of truth for xVT feature access, shared by the two choke points that gate it:
 * the route guard (`XptXvtAccessWrapper` in src/app/XPTBusinessGroupWrapper.tsx) and the
 * "xVT Planning" side-nav section (`buildBusinessGroupNavigationItems` in
 * src/features/business-group/business-group-side-navigation/utils/NavigationHelpers.ts).
 * Lives in src/utils/ so the app layer can use it without importing feature internals.
 */

import { BUSINESS_GROUP_CODES } from 'src/constants/business-group-constants';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { STAGES } from 'src/constants/generic-constants';
import { XVT_COST_CENTERS } from 'src/constants/xvt-constants';

export interface XvtAccessParams {
  /** `data_classification_short_description` of the business group the user is currently in. */
  currentBusinessGroupCode: string;
  /** Developer LDAP group membership from `useAuth()` — per-user, so it holds in prod too. */
  isDev: boolean;
  /** Role flags for the user's access record in the current business group. */
  isFinanceAdmin: boolean;
  isBudgetLeader: boolean;
  isBudgetOwner: boolean;
  /** Cost centers assigned to the user in the current business group. */
  authorizedCostCenters: readonly string[];
}

/**
 * xVT is HDW-only in every stage. Alpha and beta use normal access management: Finance Admin,
 * Budget Leader, or Budget Owner plus access to either xVT-owned cost center (7802 or 7113).
 * Prod remains developer-only until the production launch gate is removed.
 */
export const canAccessXvt = ({
  currentBusinessGroupCode,
  isDev,
  isFinanceAdmin = false,
  isBudgetLeader = false,
  isBudgetOwner = false,
  authorizedCostCenters = []
}: XvtAccessParams): boolean => {
  if (currentBusinessGroupCode !== BUSINESS_GROUP_CODES.OPD_HARDWARE) {
    return false;
  }

  const stage = ENVIRONMENT_VARIABLES.env.Stage;
  if (stage === STAGES.PROD) {
    return isDev;
  }

  if (stage !== STAGES.ALPHA && stage !== STAGES.BETA) {
    return isDev;
  }

  const hasEligibleRole = isFinanceAdmin || isBudgetLeader || isBudgetOwner;
  const hasXvtCostCenterAccess = authorizedCostCenters.some((costCenter) => XVT_COST_CENTERS.includes(costCenter));
  return hasEligibleRole && hasXvtCostCenterAccess;
};

/**
 * Whether the xVT cost-center read-only rule is enforced in the HDW Forecast Input tab.
 *
 * Unlike `canAccessXvt`, the read-only rule keys off Cost Center alone, so it would lock rows
 * in the xVT-owned cost centers for EVERY HDW user the moment this reaches prod. Stay off in prod
 * until the production access gate matches alpha/beta; dev/alpha/beta keep enforcing.
 *
 * Reads the stage at call time: module-load capture would bake in the unsubstituted
 * 'alpha' placeholder.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * 🚩 TEMPORARY LAUNCH GATE — DELETE THIS FUNCTION AT xVT LAUNCH
 *
 * This exists only to keep the read-only rule inert in prod while xVT is still developer-only
 * there. At launch, xVT cost centers must be read-only in EVERY stage, prod included.
 *
 * To flip at launch:
 *   1. Delete this function and its `describe` block in `__tests__/xvtAccessUtils.test.ts`.
 *   2. Remove the guard `if (!isXvtReadOnlyEnforced()) return false;` and its import from
 *      `features/business-group/forecast-template/forecast-utils/column-generator/helpers/AuthorizationHelper.ts`,
 *      plus the prod-stage `describe` block in that file's test.
 *   3. Delete `forecast-utils/ValidationVisibilityUtils.ts` (+ its test) and drop the
 *      `.filter(isValidationKeyVisible)` from `components/ValidationStatusDisplay.tsx`, so the
 *      xVT row shows in the upload panel again.
 *   4. Replace `canAccessXvt`'s prod `isDev` branch with the alpha/beta role + cost-center rule —
 *      otherwise prod users get rows locked in HDW while the xVT Planning tab stays invisible.
 *
 * Step 4 is the one that matters: shipping 1-3 without it recreates the exact bug this gate was
 * added to prevent (rows editable nowhere in prod).
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */
export const isXvtReadOnlyEnforced = (): boolean => ENVIRONMENT_VARIABLES.env.Stage !== STAGES.PROD;
